import { useState } from "react";
import {
  searchByName,
  weekdayShort,
  type GroupPerson,
  type LevelWeeklyTime,
  type LinkNewParticipantRequest,
  type OwnerParent,
  type OwnerWaiting,
  type ParentAsk,
  type ParentClaim,
  type ParentMatch,
} from "@teamna/shared";
import { useAskCoach, useLinkNewParticipant, useRemoveNewParticipant, useUnlinkNewParticipant } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Button, Checkbox, Field, showToast } from "../components/index.ts";
import { groupColor } from "../lib/group-color.ts";
import { WhatsAppIcon } from "../components/PhoneSignIn.tsx";
import { digitsMatch, phoneText } from "./families/model.ts";
import { daysFor } from "./new-participants-days.ts";
import { relationWord, useClaimActions, type ClaimPairs } from "./new-participants-ask.tsx";

/**
 * The cards of `/owner/new-participants` (link-parents-sync): one waiting name with its days and
 * every way to link it, and the parent picker that searches the families and the synced group.
 */

export type LinkTarget = LinkNewParticipantRequest["to"];

export { daysFor } from "./new-participants-days.ts";

/**
 * Links names one at a time (each link changes the families the next one reads), then shows one
 * toast with Undo. Undo unlinks the names this call linked.
 */
export function useLinkWithUndo(businessId: string) {
  const link = useLinkNewParticipant(businessId);
  const unlink = useUnlinkNewParticipant(businessId);
  const [busy, setBusy] = useState(false);

  type Job = { rows: OwnerWaiting[]; to: LinkTarget; via?: "owner" | "match"; matchId?: string; weekdaysOf?: Map<string, number[]> };

  /** Several links (one job per parent), one toast, one Undo for all of them. */
  async function linkJobs(jobs: Job[], done: string) {
    setBusy(true);
    const linked: string[] = [];
    let problem: unknown = null;
    for (const job of jobs) {
      let to = job.to;
      for (const row of job.rows) {
        const weekdays = job.weekdaysOf?.get(row.id) ?? daysFor(row);
        try {
          // oxlint-disable-next-line no-await-in-loop -- one link at a time, see above
          const result = await link.mutateAsync({ id: row.id, weekdays, to, ...(job.via ? { via: job.via } : {}), ...(job.matchId ? { matchId: job.matchId } : {}) });
          linked.push(row.id);
          // The first link made the family: the siblings join it, not a second new parent.
          if (to.kind === "new_family" || to.kind === "contact") to = { kind: "family", familyId: result.familyId };
        } catch (error) {
          problem = error;
        }
      }
    }
    setBusy(false);
    if (linked.length === 0) {
      showToast(problem ? messageForError(problem) : "Nothing linked.", { tone: "info" });
      return;
    }
    showToast(problem ? `${done} Some could not be linked: ${messageForError(problem)}` : done, {
      action: {
        label: "Undo",
        onClick: () => {
          void (async () => {
            for (const id of linked) {
              // oxlint-disable-next-line no-await-in-loop -- one at a time, like the links
              await unlink.mutateAsync(id).catch((error: unknown) => showToast(messageForError(error), { tone: "info" }));
            }
          })();
        },
      },
    });
  }

  const linkNames = (rows: OwnerWaiting[], to: LinkTarget, options: Omit<Job, "rows" | "to"> & { done: string }) =>
    linkJobs([{ rows, to, ...options }], options.done);

  return { linkNames, linkJobs, busy: busy || link.isPending };
}

/** The weekly times of a level, one chip a day: "Mon 18:00, Rami". */
function dayChips(times: LevelWeeklyTime[]): Array<{ weekday: number; label: string }> {
  const byDay = new Map<number, string[]>();
  for (const t of times) {
    const list = byDay.get(t.weekday) ?? [];
    list.push(`${t.startTime}${t.staffName ? `, ${t.staffName.split(" ")[0]}` : ""}`);
    byDay.set(t.weekday, list);
  }
  return [...byDay.entries()].sort((a, b) => a[0] - b[0]).map(([weekday, parts]) => ({ weekday, label: `${weekdayShort(weekday)} ${parts.join(" and ")}` }));
}

/** A person to link to: a family on the list, or a person of the synced group who is not a parent yet. */
export type PickedParent = { target: LinkTarget; name: string };

export function personLabel(person: GroupPerson): string {
  return person.name ?? phoneText(person.phoneE164);
}

/**
 * Search a parent: the families, and the people of the synced group who are not a parent yet. A
 * person of the group becomes a parent at the link. "Not in the group" adds a new parent.
 */
export function ParentPicker({
  parents,
  people,
  busy,
  disabled,
  onPick,
  onCancel,
  defaultName = "",
  defaultPhone = "",
}: {
  parents: OwnerParent[];
  people: GroupPerson[];
  busy: boolean;
  disabled?: boolean;
  onPick: (picked: PickedParent) => void;
  onCancel?: () => void;
  defaultName?: string;
  defaultPhone?: string;
}) {
  const { label } = useLabels();
  const member = label("member");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const parentPhones = new Set(parents.map((p) => p.phoneE164).filter(Boolean));
  // The name search rule (other spellings too, best first), or the last digits of the mobile.
  const parentHits = searchByName(parents, q, (p) => [p.name, ...p.childNames.map((child) => ({ text: child, via: true as const }))], {
    extra: (p) => digitsMatch(p.phoneE164, q),
  });
  const groupHits = searchByName(
    people.filter((p) => !p.isParent && !parentPhones.has(p.phoneE164)),
    q,
    (p) => [p.name, p.whatsappName],
    { extra: (p) => digitsMatch(p.phoneE164, q) },
  );
  const searched = parentHits.searched;
  const families = searched ? parentHits.rows.slice(0, 6) : [];
  const group = searched ? groupHits.rows.slice(0, 8) : [];
  useTrackNameSearch("new_participant_cards", q, [...parentHits.results, ...groupHits.results], families.length + group.length);

  return (
    <div className="flex flex-col gap-2 border-t border-dashed border-line-strong bg-sunken px-4 py-3">
      <div className="max-w-md">
        <Field label={`Search a ${member}`} placeholder="A name, or the last digits of the mobile" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {families.map((p) => (
        <PickRow
          key={p.familyId}
          title={p.name}
          phone={p.phoneE164}
          detail={p.childNames.length ? `${label("participant", { plural: true, capital: true })}: ${p.childNames.join(", ")}` : `On your list, no ${label("participant")} yet`}
          busy={busy}
          disabled={disabled}
          onPick={() => onPick({ target: { kind: "family", familyId: p.familyId }, name: p.name })}
        />
      ))}
      {group.map((p) => (
        <PickRow
          key={p.id}
          title={personLabel(p)}
          phone={p.phoneE164}
          detail={p.whatsappName && p.whatsappName !== p.name ? `In the WhatsApp group as ${p.whatsappName}` : "In the WhatsApp group"}
          busy={busy}
          disabled={disabled}
          onPick={() => onPick({ target: { kind: "contact", contactId: p.id }, name: personLabel(p) })}
        />
      ))}
      {searched && families.length === 0 && group.length === 0 ? <p className="text-[13px] text-ink-2">Nobody found. Add the {member} below.</p> : null}
      {adding ? (
        <NewParentForm
          defaultName={defaultName}
          defaultPhone={defaultPhone}
          disabled={Boolean(disabled)}
          busy={busy}
          onSave={(parentName, phone) => onPick({ target: { kind: "new_family", parentName, phone }, name: parentName || phone })}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            Not in the group: add a {member}
          </Button>
          {onCancel ? (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function PickRow({ title, phone, detail, busy, disabled, onPick }: { title: string; phone: string | null; detail: string; busy: boolean; disabled?: boolean; onPick: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-card border border-line bg-paper px-3 py-2">
      <div className="min-w-0 flex-1 text-[13.5px]">
        <p className="font-semibold text-ink">
          {title}
          {phone ? <span className="font-normal text-ink-2">, {phoneText(phone)}</span> : null}
        </p>
        <p className="text-[12.5px] text-ink-2">{detail}</p>
      </div>
      <Button size="sm" disabled={disabled} loading={busy} onClick={onPick}>
        Link
      </Button>
    </div>
  );
}

function NewParentForm({
  defaultName,
  defaultPhone,
  disabled,
  busy,
  onSave,
}: {
  defaultName: string;
  defaultPhone: string;
  disabled: boolean;
  busy: boolean;
  onSave: (name: string, phone: string) => void;
}) {
  const { label } = useLabels();
  const Member = label("member", { capital: true });
  const [name, setName] = useState(defaultName);
  const [phone, setPhone] = useState(defaultPhone);
  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-paper px-3 py-3">
      <div className="grid max-w-xl gap-3 sm:grid-cols-2">
        {/* A name is not needed: the parent shows as the number until they add their own name in the
            app. Better than "Kareem Mom", which the parent sees when they join. */}
        <Field
          label={`${Member} name (optional)`}
          hint="Not sure? Leave it empty. They add their own name when they join Teamna."
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Field label={`${Member} mobile`} value={phone} inputMode="tel" placeholder="+971" onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div>
        <Button disabled={disabled || !phone.trim()} loading={busy} onClick={() => onSave(name.trim(), phone.trim())}>
          Add {label("member")} and link
        </Button>
      </div>
    </div>
  );
}

function SelfForm({ name, disabled, busy, onSave }: { name: string; disabled: boolean; busy: boolean; onSave: (phone: string) => void }) {
  const { label } = useLabels();
  const member = label("member");
  const [phone, setPhone] = useState("");
  const first = name.split(" ")[0];
  return (
    <div className="flex flex-col gap-3 border-t border-dashed border-line-strong bg-sunken px-4 py-3">
      <p className="text-[13.5px] text-ink-2">{first} gets their own account and books for themselves. There is no {member}.</p>
      <div className="max-w-xs">
        <Field label={`${first}'s mobile`} value={phone} inputMode="tel" placeholder="+971" onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div>
        <Button disabled={disabled || !phone.trim()} loading={busy} onClick={() => onSave(phone.trim())}>
          Make own account
        </Button>
      </div>
    </div>
  );
}

type Form = "pick" | "self" | "remove" | null;

/**
 * One waiting name in "Still missing": what Teamna knows (the coach's note, a match), the days,
 * and every way to link it. A sibling or a match links on the coach's days.
 */
export function WaitingCard({
  businessId,
  row,
  parents,
  people,
  match,
  siblings,
  ai,
  looked,
  claims = [],
  pairs,
  waiting,
  posted = false,
  coachAsk,
}: {
  businessId: string;
  row: OwnerWaiting;
  parents: OwnerParent[];
  people: GroupPerson[];
  match?: ParentMatch;
  /** The other waiting names Teamna thinks are this swimmer's siblings. */
  siblings: OwnerWaiting[];
  /** The matches come from the AI (else the family-name rule). */
  ai: boolean;
  /** Teamna looked in the group at least once. */
  looked: boolean;
  /** Answers of parents or coaches for this name (or its siblings), open and decided. */
  claims?: ParentClaim[];
  /** Clash and two-parent answers among all the open answers of the page. */
  pairs: ClaimPairs;
  /** Every waiting name of the page, for the days of the names an answer names. */
  waiting: OwnerWaiting[];
  /** The owner asked the parents in the group. */
  posted?: boolean;
  /** The latest question to a coach about this name (migration 0046). */
  coachAsk?: ParentAsk;
}) {
  const { label } = useLabels();
  const { linkNames, busy } = useLinkWithUndo(businessId);
  const remove = useRemoveNewParticipant(businessId);
  const [weekdays, setWeekdays] = useState<number[]>(daysFor(row));
  const [form, setForm] = useState<Form>(null);
  const [withSiblings, setWithSiblings] = useState(true);
  const [editDays, setEditDays] = useState(false);
  const one = label("participant");
  const member = label("member");
  const chips = dayChips(row.weeklyTimes);
  // A level with no weekly time yet links with no days; the owner picks them later.
  const ready = weekdays.length > 0 || chips.length === 0;
  const parentOf = (familyId: string) => parents.find((p) => p.familyId === familyId);
  const first = row.name.split(" ")[0];
  const matchPerson = match?.contactId ? people.find((p) => p.id === match.contactId) : undefined;
  const who = ai ? "The AI" : "Teamna";
  const coaches = row.coachNames.map((n) => n.split(" ")[0]).join(" and ") || row.addedByName?.split(" ")[0] || `a ${label("staff")}`;
  const together = siblings.length > 0 && withSiblings;
  // Every waiting name of the page: a pair's answers can name a child that is not on this card (review F4.2).
  const claimActions = useClaimActions(businessId, waiting);
  const askCoach = useAskCoach(businessId);
  // The coach the server asks (review F1.3): the button names them and sends them back.
  const coachFirst = row.askCoach ? (row.askCoach.name.split(" ")[0] ?? null) : null;
  const askedFirst = coachAsk ? (coachAsk.coachName.split(" ")[0] ?? coachFirst) : coachFirst;
  const sendAsk = () =>
    askCoach.mutate({ pendingIds: together ? [row.id, ...siblings.map((s) => s.id)] : [row.id], coachUserId: row.askCoach!.userId }, {
      onSuccess: (result) => {
        const coach = result.coachName.split(" ")[0];
        showToast(
          result.whatsappStatus === "sent"
            ? `Teamna sent ${coach} a WhatsApp with a link to the question.`
            : `${coach} sees the question in Teamna. WhatsApp did not go out (${result.whatsappStatus === "no_phone" ? "no mobile number" : result.whatsappStatus === "not_configured" ? "WhatsApp is not set up" : "it failed"}).`,
          result.whatsappStatus === "sent" ? {} : { tone: "info" },
        );
      },
      onError: (error) => showToast(messageForError(error), { tone: "info" }),
    });
  const openClaims = claims.filter((c) => c.status === "new");
  const clash = openClaims.some((c) => pairs.isClash(c));
  // A mother and a father answered for this name (or a sibling): one Link both for each pair.
  const groups = clash ? [] : [...new Map(openClaims.map((c) => pairs.groupOf(c)).filter((g) => g.length > 0).map((g) => [g.map((c) => c.id).sort().join(), g])).values()];
  const inGroup = new Set(groups.flat().map((c) => c.id));

  const send = (to: LinkTarget, whoName: string, via: "owner" | "match" = "owner") => {
    // A days-only save or an adult's own account is for this name alone.
    const names = to.kind === "participant" || to.kind === "self" || !withSiblings ? [row] : [row, ...siblings];
    void linkNames(names, to, {
      via,
      ...(via === "match" && match ? { matchId: match.id } : {}),
      weekdaysOf: new Map([[row.id, weekdays]]),
      done: `${names.map((n) => n.name.split(" ")[0]).join(" and ")} linked to ${whoName.split(" ")[0]}.`,
    });
    setForm(null);
  };

  const toggle = (weekday: number) => setWeekdays((current) => (current.includes(weekday) ? current.filter((d) => d !== weekday) : [...current, weekday].sort()));

  // What Teamna tried for this name, one line each: the answers first.
  // `note`: what the parent wrote, shown after the text in its own direction (it can be in Arabic).
  const tried: Array<{ mark: string; text: string; note?: string }> = [];
  if (clash) tried.push({ mark: "!", text: `Two ${label("member", { plural: true })} gave the same child. Pick the right one, or link both as one family.` });
  else if (groups.length) tried.push({ mark: "✓", text: "The mother and the father both answered. Link both puts them in one family." });
  for (const c of openClaims) {
    tried.push({
      mark: "💬",
      text:
        c.source === "coach" && c.coachName
          ? `${c.coachName.split(" ")[0]} says ${c.name} is the ${member}. ${phoneText(c.phoneE164)}`
          : `${c.name}${relationWord(c) ? ` (${relationWord(c)!.toLowerCase()})` : ""} says this is their child. ${phoneText(c.phoneE164)}`,
      ...(c.note ? { note: c.note } : {}),
    });
  }
  for (const c of claims.filter((x) => x.status === "rejected")) tried.push({ mark: "✕", text: `You said no to ${c.name}.` });
  if (match && matchPerson) {
    tried.push({ mark: "✦", text: `${who} says ${match.confidence === "likely" ? "likely" : "maybe"} ${personLabel(matchPerson)}: ${match.reason}` });
  } else if (match?.confidence === "siblings") {
    tried.push({ mark: "✦", text: match.reason });
  } else if (row.suggestions.length === 0) {
    tried.push(looked ? { mark: "✕", text: `${who} found no name in the group that fits.` } : { mark: "·", text: "Teamna did not look in the group yet. Use AI matches." });
  }
  for (const s of row.suggestions) {
    const parent = parentOf(s.familyId);
    if (!parent) continue;
    tried.push({
      mark: s.sure ? "✓" : "·",
      text:
        s.reason === "mobile"
          ? `The mobile ${row.addedByName?.split(" ")[0] ?? `the ${label("staff")}`} wrote is the mobile of ${parent.name}.`
          : `Same family name as ${parent.name}${parent.childNames.length ? ` (${label("participant", { plural: true })}: ${parent.childNames.join(", ")})` : ""}.`,
    });
  }

  if (!openClaims.length && !row.participant) {
    tried.push(posted ? { mark: "·", text: "Asked in the group. No answer yet." } : { mark: "·", text: "Not asked in the group yet." });
  }
  if (coachAsk?.status === "open") tried.push({ mark: "·", text: `You asked ${askedFirst} on WhatsApp. Waiting for an answer.` });
  else if (coachAsk?.status === "dont_know") tried.push({ mark: "✕", text: `You asked ${askedFirst}. ${askedFirst} does not know the ${member}.` });
  const canAskCoach = !row.participant && coachFirst !== null && coachAsk?.status !== "open" && !(coachAsk?.status === "answered" && openClaims.length);

  return (
    <article id={`waiting-${row.id}`} className="overflow-hidden rounded-card border border-line bg-paper" aria-label={row.name}>
      <div className="flex gap-3 px-4 py-3">
        <span className="mt-1 h-8 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: groupColor(row.groupColor, 0) }} aria-hidden="true" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15.5px] font-bold text-ink">{together ? [row, ...siblings].map((n) => n.name).join(", ") : row.name}</h3>
            {together ? <span className="rounded-full bg-sunken px-2 py-0.5 text-[11.5px] font-bold text-ink-2">Siblings</span> : null}
            <span
              className={`rounded-full px-2 py-0.5 text-[11.5px] font-bold ${
                row.participant
                  ? "bg-accent-surface text-accent-strong"
                  : clash
                    ? "bg-cancelled-surface text-cancelled"
                    : openClaims.length
                      ? "bg-confirmed-surface text-whatsapp"
                      : "bg-late-surface text-late-ink"
              }`}
            >
              {row.participant ? `Has a ${member}` : clash ? "Clash" : groups.length ? `Two ${label("member", { plural: true })}` : openClaims.length ? "Check" : "To find"}
            </span>
          </div>
          <p className="text-[13px] text-ink-2">
            {row.groupName} · {label("staff")} {coaches}
            {chips.length > 0 ? (
              <>
                {" · "}Days: {weekdays.length ? weekdays.map((d) => weekdayShort(d)).join(", ") : "none"}{" "}
                <button type="button" className="font-semibold text-accent-strong underline" aria-expanded={editDays} onClick={() => setEditDays(!editDays)}>
                  {editDays ? "Done" : "Change"}
                </button>
              </>
            ) : null}
          </p>
          {editDays && chips.length > 0 ? (
            <fieldset className="flex flex-wrap gap-2">
              <legend className="sr-only">Days in {row.groupName}</legend>
              {chips.map((chip) => (
                <button
                  key={chip.weekday}
                  type="button"
                  aria-pressed={weekdays.includes(chip.weekday)}
                  onClick={() => toggle(chip.weekday)}
                  className={`min-h-9 rounded-full border px-3 text-[13px] font-semibold ${
                    weekdays.includes(chip.weekday) ? "border-accent bg-accent-surface text-accent-strong" : "border-line-strong bg-paper text-ink-2"
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </fieldset>
          ) : null}
          {!ready ? <p className="text-[12.5px] text-late-ink">Pick at least one day.</p> : null}
          {(row.visits ?? []).length ? (
            <p className="flex flex-wrap gap-1.5">
              {(row.visits ?? []).map((visit) => (
                <span key={visit.sessionId} className="rounded-full bg-accent-surface px-2.5 py-0.5 text-[12px] font-semibold text-accent-strong">
                  Came {formatDateTime(visit.startsAt)}. Not booked.
                </span>
              ))}
            </p>
          ) : null}
          {row.noteParentName || row.notePhoneE164 ? (
            <p className="self-start rounded-lg bg-sunken px-2.5 py-1 text-[13px] text-ink">
              <span className="text-ink-3">Note from {coaches}: </span>
              {[row.noteParentName ? `${member} ${row.noteParentName}` : "", row.notePhoneE164 ? phoneText(row.notePhoneE164) : ""].filter(Boolean).join(", ")}
            </p>
          ) : null}
          {!row.participant && tried.length ? (
            <ul className="flex flex-col gap-0.5 text-[13px] text-ink-2">
              {tried.map((t) => (
                <li key={t.text} className="flex gap-2">
                  <span aria-hidden="true" className="w-3 shrink-0 text-ink-3">
                    {t.mark}
                  </span>
                  <span>
                    {t.text}
                    {t.note ? (
                      <>
                        . Note: <bdi dir="auto" className="[overflow-wrap:anywhere]">{t.note}</bdi>
                      </>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {!row.participant && siblings.length > 0 ? (
            <Checkbox
              label={`Link ${siblings.map((s) => s.name.split(" ")[0]).join(", ")} too (likely ${siblings.length === 1 ? "a sibling" : "siblings"})`}
              checked={withSiblings}
              onChange={setWithSiblings}
            />
          ) : null}
          {row.participant ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="min-w-0 flex-1 text-[13.5px] text-ink">
                {first} already has a {member}: {parentOf(row.participant.familyId)?.name ?? "on your list"}. Save the days to add {row.coachNames.join(" and ")}.
              </p>
              <Button disabled={!ready} loading={busy} onClick={() => send({ kind: "participant" }, parentOf(row.participant!.familyId)?.name ?? member)}>
                Save the days
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {groups.map((group) => (
                <Button key={group.map((c) => c.id).join()} size="sm" loading={claimActions.busy} onClick={() => void claimActions.linkBoth(group)}>
                  Link both: {[...new Set(group.map((c) => c.name.split(" ")[0]))].join(" and ")}
                </Button>
              ))}
              {openClaims
                .filter((c) => !inGroup.has(c.id))
                .map((c) => (
                  <Button key={c.id} size="sm" loading={claimActions.busy} onClick={() => claimActions.linkOne(c)}>
                    Link to {c.name.split(" ")[0]}
                  </Button>
                ))}
              {match && matchPerson ? (
                <Button
                  variant={match.confidence === "likely" ? "primary" : "secondary"}
                  size="sm"
                  disabled={!ready}
                  loading={busy}
                  onClick={() => send({ kind: "contact", contactId: matchPerson.id }, personLabel(matchPerson), "match")}
                >
                  Link to {personLabel(matchPerson)}
                </Button>
              ) : null}
              {row.suggestions.map((sg) => {
                const parent = parentOf(sg.familyId);
                if (!parent) return null;
                return (
                  <Button key={sg.familyId} variant={sg.sure ? "primary" : "secondary"} size="sm" disabled={!ready} loading={busy} onClick={() => send({ kind: "family", familyId: sg.familyId }, parent.name)}>
                    Link to {parent.name}
                  </Button>
                );
              })}
              {canAskCoach ? (
                <Button variant="whatsapp" size="sm" loading={askCoach.isPending} onClick={sendAsk}>
                  <WhatsAppIcon className="size-4" />
                  {coachAsk?.status === "dont_know" ? `Ask ${coachFirst} again` : `Ask ${coachFirst} on WhatsApp`}
                </Button>
              ) : null}
              <Button variant={form === "pick" ? "primary" : "secondary"} size="sm" aria-expanded={form === "pick"} onClick={() => setForm(form === "pick" ? null : "pick")}>
                Pick a {member}
              </Button>
              <Button variant="ghost" size="sm" aria-expanded={form === "self"} onClick={() => setForm(form === "self" ? null : "self")}>
                Adult: own account
              </Button>
              <Button variant="ghost" size="sm" aria-expanded={form === "remove"} onClick={() => setForm(form === "remove" ? null : "remove")}>
                Not our {one}
              </Button>
            </div>
          )}
        </div>
      </div>

      {form === "pick" ? (
        <ParentPicker
          parents={parents}
          people={people}
          busy={busy}
          disabled={!ready}
          defaultName={row.noteParentName ?? ""}
          defaultPhone={row.notePhoneE164 ?? ""}
          onPick={(picked) => send(picked.target, picked.name)}
          onCancel={() => setForm(null)}
        />
      ) : null}
      {form === "self" ? (
        <SelfForm name={row.name} disabled={!ready} busy={busy} onSave={(phone) => send({ kind: "self", phone }, row.name)} />
      ) : null}
      {form === "remove" ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-dashed border-line-strong bg-sunken px-4 py-3">
          <p className="min-w-0 flex-1 text-[13.5px] text-ink">
            Remove {row.name}? The name goes off the list of {row.coachNames.join(" and ") || `the ${label("staff")}`}. Nobody gets a message.
          </p>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(row.id, { onSuccess: () => showToast(`${row.name} removed.`) })}>
            Remove
          </Button>
          <Button variant="secondary" onClick={() => setForm(null)}>
            Keep
          </Button>
        </div>
      ) : null}
    </article>
  );
}
